import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";

// AppFillip sales WhatsApp — E.164 format, no + prefix
const WHATSAPP_NUMBER = "919950510511";

const GREETING_DELAY_MS = 30000;     // popup greeting after 30s
const SESSION_DISMISSED = "appfillip_wa_greeting_dismissed";

const SUPPRESS_ON = [
  /^\/audit\//,
];

const QUICK_REPLIES = [
  { label: "Get a free ASO audit", text: "Hi AppFillip — I'd like a free ASO audit for my app." },
  { label: "Pricing for my market", text: "Hi AppFillip — can you share pricing for app marketing in my country?" },
  { label: "Talk to a strategist", text: "Hi AppFillip — I'd like to book a call with a strategist." },
];

function buildLink(text) {
  const base = `https://wa.me/${WHATSAPP_NUMBER}`;
  return text ? `${base}?text=${encodeURIComponent(text)}` : base;
}

export default function WhatsAppBubble() {
  const location = useLocation();
  const [openPanel, setOpenPanel] = useState(false);
  const [greetingOpen, setGreetingOpen] = useState(false);

  useEffect(() => {
    if (SUPPRESS_ON.some((rx) => rx.test(location.pathname))) return;

    let dismissed = false;
    try { dismissed = !!sessionStorage.getItem(SESSION_DISMISSED); } catch {}
    if (dismissed) return;

    const t = setTimeout(() => setGreetingOpen(true), GREETING_DELAY_MS);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const dismissGreeting = () => {
    setGreetingOpen(false);
    try { sessionStorage.setItem(SESSION_DISMISSED, "1"); } catch {}
  };

  const handleBubbleClick = () => {
    setOpenPanel((s) => !s);
    if (greetingOpen) dismissGreeting();
  };

  const suppress = SUPPRESS_ON.some((rx) => rx.test(location.pathname));
  if (suppress) return null;

  return (
    <div className="fixed bottom-5 right-5 z-50 print:hidden" data-testid="wa-bubble">
      {/* Greeting bubble — auto-shows after 30s */}
      <AnimatePresence>
        {greetingOpen && !openPanel && (
          <motion.div
            key="greeting"
            initial={{ opacity: 0, y: 12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 280, damping: 24 }}
            className="absolute bottom-[72px] right-0 w-[min(calc(100vw-40px),280px)] bg-white border-2 border-charcoal shadow-[6px_6px_0_0_#25D366] p-4"
            data-testid="wa-greeting"
          >
            <button
              type="button"
              aria-label="Close greeting"
              onClick={dismissGreeting}
              className="absolute top-2 right-2 text-zinc-400 hover:text-charcoal"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-7 h-7 grid place-items-center bg-[#25D366] rounded-full">
                <MessageCircle className="w-3.5 h-3.5 text-white fill-white" />
              </span>
              <p className="text-overline text-zinc-500">Live · Mon–Fri</p>
            </div>
            <p className="font-display text-sm font-extrabold text-charcoal leading-tight mb-1">
              Hey 👋 Got an app you want to grow?
            </p>
            <p className="text-xs text-zinc-600 leading-relaxed">
              Senior strategists are online. Tap to start a WhatsApp chat.
            </p>
            <button
              type="button"
              onClick={handleBubbleClick}
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#1ebe57] text-white text-xs font-bold uppercase tracking-widest px-3 py-2 transition-colors"
            >
              Start chat <Send className="w-3 h-3" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick-reply panel */}
      <AnimatePresence>
        {openPanel && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 280, damping: 24 }}
            className="absolute bottom-[72px] right-0 w-[min(calc(100vw-40px),320px)] bg-white border-2 border-charcoal shadow-[8px_8px_0_0_#25D366] overflow-hidden"
            data-testid="wa-panel"
          >
            {/* Header */}
            <div className="bg-[#25D366] text-white px-4 py-3 flex items-center justify-between">
              <div>
                <p className="font-bold text-sm leading-tight">AppFillip Sales</p>
                <p className="text-[11px] text-white/80 leading-tight">Replies in ~5 min</p>
              </div>
              <button
                type="button"
                onClick={() => setOpenPanel(false)}
                aria-label="Close"
                className="text-white/80 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4">
              <p className="text-xs text-zinc-600 leading-relaxed mb-3">
                Pick a starter — we'll continue the conversation in WhatsApp.
              </p>
              <div className="space-y-2">
                {QUICK_REPLIES.map((q) => (
                  <a
                    key={q.label}
                    href={buildLink(q.text)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpenPanel(false)}
                    className="block bg-white border border-zinc-200 hover:border-[#25D366] hover:bg-[#25D366]/5 px-3 py-2.5 text-sm text-charcoal transition-colors"
                    data-testid={`wa-reply-${q.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {q.label}
                  </a>
                ))}
              </div>

              <a
                href={buildLink()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpenPanel(false)}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-charcoal text-white px-3 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-electric transition-colors"
              >
                <Send className="w-3 h-3" /> Open WhatsApp
              </a>
              <p className="mt-3 text-[10px] text-zinc-400 text-center">
                Powered by WhatsApp · no app install required
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bubble button */}
      <button
        type="button"
        onClick={handleBubbleClick}
        aria-label={openPanel ? "Close WhatsApp menu" : "Open WhatsApp chat"}
        className="relative w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1ebe57] grid place-items-center shadow-[4px_4px_0_0_#2D2D2D] hover:shadow-[6px_6px_0_0_#2D2D2D] hover:-translate-y-0.5 transition-all"
        data-testid="wa-bubble-trigger"
      >
        {!openPanel && (
          <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" aria-hidden />
        )}
        {openPanel ? (
          <X className="w-6 h-6 text-white relative" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white fill-white relative" />
        )}
        {greetingOpen && !openPanel && (
          <span
            className="absolute -top-1 -right-1 w-3 h-3 bg-electric border-2 border-white rounded-full"
            aria-hidden
          />
        )}
      </button>
    </div>
  );
}
